import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import VehicleForm from '../components/VehicleForm';
import { confirmDialog } from '../utils/confirm';
import EventForm from '../components/EventForm';
import DocumentForm from '../components/DocumentForm';
import AccidentForm from '../components/AccidentForm';
import CostForm from '../components/CostForm';
import Modal from '../components/Modal';
import CustomSelect from '../components/CustomSelect';
import VehicleStatsContent from '../components/VehicleStatsContent';
import DocViewer from '../components/DocViewer';
import { costTypeColor } from '../utils/costTypes';
import InspectionForm from '../components/InspectionForm';
import SubAssignForm from '../components/SubAssignForm';
import HoverPopover from '../components/HoverPopover';


// Matches the getFileUrl() helper in Accidents.jsx / Costs.jsx — attachments for
// these two record types aren't served through the CORS-safe /api/static/ route
// (only Documents got that fix), so this mirrors the same existing behavior.
const getAttachmentUrl = (filename, folder) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/${folder}/${filename}`;
};

const getAttachmentsList = (record) => (record?.attachments?.length ? record.attachments : (record?.attachment?.filename ? [record.attachment] : []));
const getAttachmentsPreview = (record, folder) => {
  const list = getAttachmentsList(record);
  return { urls: list.map((att) => getAttachmentUrl(att.filename, folder)), filenames: list.map((att) => att.originalName || att.filename) };
};

const isImageFilename = (filename) => /\.(jpg|jpeg|png)$/i.test(filename || '');

// Documents are always stored with documentType forced to ALL CAPS (backend
// normalizes it for matching/filtering). Map the well-known types back to
// their proper display casing; fall back to Title Case for custom "Other" types.
const DOC_TYPE_LABELS = { RCA: 'RCA', ITP: 'ITP', CASCO: 'CASCO', ROVINIETA: 'Rovinieta', 'ROAD ASSISTANCE': 'Road Assistance' };
const prettyDocumentType = (type) => {
  if (!type) return '';
  const upper = String(type).toUpperCase();
  if (DOC_TYPE_LABELS[upper]) return DOC_TYPE_LABELS[upper];
  return String(type).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const parseFlexDate = (val) => {
  if (!val) return null;
  const s = String(val);
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const d = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(s);
  return isNaN(d) ? null : d;
};

// % of the document's validity window that has elapsed (100 = expired).
const computeValidityPercent = (issuedVal, expiryVal, daysLeft) => {
  if (daysLeft == null || isNaN(daysLeft)) return null;
  if (daysLeft < 0) return 100;
  const issuedDate = parseFlexDate(issuedVal);
  const expDate = parseFlexDate(expiryVal);
  let totalDays = 365;
  if (issuedDate && expDate) {
    const diff = Math.round((expDate - issuedDate) / (1000 * 60 * 60 * 24));
    if (diff > 0) totalDays = diff;
  }
  const elapsed = Math.max(0, totalDays - daysLeft);
  return Math.max(0, Math.min(100, Math.round((elapsed / totalDays) * 100)));
};

const DocHoverContent = ({ label, sublabel, hasFile, issuer, issued, exp, documentNumber, notes, daysLeft, daysColor, daysLabel, thumbUrl, isImageFile, fileCount }) => {
  const percent = computeValidityPercent(issued, exp, daysLeft);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-1)' }}>{label}</div>
        {daysLabel ? (
          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: daysColor, background: `${daysColor}18`, whiteSpace: 'nowrap' }}>{daysLabel}</span>
        ) : (
          <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, color: '#9ca3af', background: 'var(--surface-3)', whiteSpace: 'nowrap' }}>No expiry</span>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>{sublabel}</div>

      {percent != null && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-3)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${percent}%`, borderRadius: 999, background: daysColor }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: 'var(--text-3)' }}>
            <span>Issued {issued || '—'}</span>
            <span>Expires {exp || '—'}</span>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 8 }}>
        <span style={{ color: 'var(--text-3)' }}>Issuer</span>
        <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{issuer && issuer !== '—' ? issuer : 'On file'}</span>
      </div>

      {documentNumber && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 8 }}>
          <span style={{ color: 'var(--text-3)' }}>Document No.</span>
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{documentNumber}</span>
        </div>
      )}

      {notes && (
        <div style={{ fontSize: 11, color: 'var(--text-2)', marginBottom: 10, lineHeight: 1.4, fontStyle: 'italic' }}>"{notes}"</div>
      )}

      <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        {hasFile ? (
          isImageFile && thumbUrl ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <img src={thumbUrl} alt={label} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }} />
              <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>{fileCount > 1 ? `${fileCount} files attached` : '1 file attached'}<br />Click Preview to view</div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{fileCount > 1 ? `${fileCount} files attached` : 'File attached'} — click Preview to view</div>
          )
        ) : (
          <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⚠ No file attached</div>
        )}
      </div>
    </div>
  );
};

const CostHoverContent = ({ cost, totalRON }) => {
  const color = costTypeColor(cost.costType);
  const amount = Number(cost.amount) || 0;
  const share = totalRON > 0 ? Math.min(100, Math.round((amount / totalRON) * 100)) : 0;
  const attachments = getAttachmentsList(cost);
  const thumbUrl = attachments[0] ? getAttachmentUrl(attachments[0].filename, 'costs') : null;
  const isImageFile = isImageFilename(attachments[0]?.filename);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
        <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-1)', textTransform: 'capitalize' }}>{cost.costType || 'Cost'}</span>
        <span style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 800, color: '#0077b6' }}>{amount.toLocaleString()}</span>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{cost.currency || 'RON'}</span>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 10 }}>
        {cost.date ? new Date(cost.date).toLocaleDateString() : '—'}
      </div>

      {cost.description && (
        <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginBottom: 10, lineHeight: 1.4 }}>{cost.description}</div>
      )}

      {cost.receiptRef && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 8 }}>
          <span style={{ color: 'var(--text-3)' }}>Receipt / Ref</span>
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{cost.receiptRef}</span>
        </div>
      )}

      {cost.company && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, marginBottom: 12 }}>
          <span style={{ color: 'var(--text-3)' }}>Company</span>
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>{cost.company}</span>
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-3)', marginBottom: 4 }}>
          <span>Share of total costs</span>
          <span style={{ fontWeight: 700, color: 'var(--text-1)' }}>{share}%</span>
        </div>
        <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-3)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${share}%`, borderRadius: 999, background: color }} />
        </div>
      </div>

      <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
        {attachments.length > 0 ? (
          isImageFile && thumbUrl ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <img src={thumbUrl} alt="receipt" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }} />
              <div style={{ fontSize: 11, color: 'var(--text-3)', lineHeight: 1.4 }}>{attachments.length > 1 ? `${attachments.length} files attached` : '1 file attached'}<br />Click Preview to view</div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{attachments.length > 1 ? `${attachments.length} files attached` : 'File attached'} — click Preview to view</div>
          )
        ) : (
          <div style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>⚠ No file attached</div>
        )}
      </div>
    </div>
  );
};

const CostsCard = ({ costs, onAdd, onPreview, onEdit, onDelete, isAdmin }) => {
  const totalRON = costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const preview = costs.slice(0, 6);
  const [costHover, setCostHover] = useState(null); // { rect, cost }
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Vehicle Costs</div>
        <button className="btn btn-primary btn-sm" onClick={onAdd}>+ Add</button>
      </div>
      <div className="card-body">
        {costs.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>No costs recorded yet.</div>
        ) : (
          <>
            {preview.map((cost, i) => {
              const color = costTypeColor(cost.costType);
              const key = cost._id || i;
              return (
                <div
                  key={key}
                  onMouseEnter={(e) => { const point = { x: e.clientX, y: e.clientY }; setCostHover((prev) => (prev?.pinned ? prev : { point, cost, key, pinned: false })); }}
                  onMouseMove={(e) => { const point = { x: e.clientX, y: e.clientY }; setCostHover((prev) => (prev && !prev.pinned && prev.key === key ? { ...prev, point } : prev)); }}
                  onMouseLeave={() => setCostHover((prev) => (prev?.pinned ? prev : null))}
                  onClick={(e) => { const point = { x: e.clientX, y: e.clientY }; setCostHover((prev) => (prev?.pinned && prev.key === key ? null : { point, cost, key, pinned: true })); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < preview.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', textTransform: 'capitalize' }}>{cost.costType || 'Cost'}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', paddingLeft: '14px' }}>
                      {cost.date ? new Date(cost.date).toLocaleDateString() : '—'}
                      {cost.description ? ` • ${cost.description.slice(0, 28)}${cost.description.length > 28 ? '…' : ''}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <div>
                      <span style={{ fontSize: '14px', fontWeight: 700, color: '#0077b6' }}>{Number(cost.amount || 0).toLocaleString()}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '3px' }}>{cost.currency || 'RON'}</span>
                    </div>
                    {getAttachmentsList(cost).length > 0 && (
                      <button
                        className="btn btn-ghost btn-xs"
                        style={{ color: '#0077b6', padding: '2px 8px', fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); onPreview(cost); }}
                      >
                        Preview
                      </button>
                    )}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-xs" style={{ color: '#059669', padding: '2px 8px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); onEdit(cost); }}>Edit</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', padding: '2px 8px', fontSize: 11 }} onClick={(e) => { e.stopPropagation(); onDelete(cost); }}>Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {costs.length > 6 && (
              <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{costs.length - 6} more</div>
            )}
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)' }}>{costs.length} entr{costs.length === 1 ? 'y' : 'ies'}</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)' }}>{totalRON.toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-3)' }}>RON</span></span>
            </div>
          </>
        )}
      </div>
      {costHover && (
        <HoverPopover point={costHover.point} pinned={costHover.pinned} onClose={() => setCostHover(null)}>
          <CostHoverContent cost={costHover.cost} totalRON={totalRON} />
        </HoverPopover>
      )}
    </div>
  );
};

const Vehicles = ({ category = 'permanent' }) => {
  const location = useLocation();
  const { isAdmin } = useAppContext();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filter, setFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showDriverProfileModal, setShowDriverProfileModal] = useState(false);
  const [showDriverHistoryModal, setShowDriverHistoryModal] = useState(false);
  const [showUserLogModal, setShowUserLogModal] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);
  const [inspectionType, setInspectionType] = useState(null); // 'checkin' | 'checkout' | null
  const [showInspectionDetails, setShowInspectionDetails] = useState(false);
  const [inspectionRecords, setInspectionRecords] = useState([]);
  const [inspectionPreview, setInspectionPreview] = useState(null);
  const [showSubAssignForm, setShowSubAssignForm] = useState(false);
  const [showSubProfile, setShowSubProfile] = useState(false);
  const [showSubLog, setShowSubLog] = useState(false);
  const [showSubDeallocate, setShowSubDeallocate] = useState(false);
  const [subDeallocateForm, setSubDeallocateForm] = useState({ returnDate: '', fuelLevelReturn: '' });
  const [drivers, setDrivers] = useState([]);
  const [vehicleCosts, setVehicleCosts] = useState([]);
  const [vehicleAccidents, setVehicleAccidents] = useState([]);
  const [vehicleAllDocs, setVehicleAllDocs] = useState([]);
  const [vehicleEvents, setVehicleEvents] = useState([]);
  const [showMoreDocs, setShowMoreDocs] = useState(false);
  const [docPreview, setDocPreview] = useState(null); // { url, label }
  const [docHover, setDocHover] = useState(null); // { rect, data }
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingAccident, setEditingAccident] = useState(null);
  const [editingCost, setEditingCost] = useState(null);
  const [docActionMsg, setDocActionMsg] = useState(null);

  const STANDARD_DOC_TYPES = ['RCA', 'ITP', 'CASCO', 'ROVINIETA', 'ROAD ASSISTANCE'];

  const refreshVehicleCosts = (plate) => {
    apiEndpoints.costs.list()
      .then(res => {
        const sorted = (res.data || [])
          .filter(c => c.plate === plate)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        setVehicleCosts(sorted);
      })
      .catch(() => setVehicleCosts([]));
  };

  const refreshVehicleAccidents = (plate) => {
    apiEndpoints.accidents.list()
      .then(res => {
        setVehicleAccidents((res.data || []).filter(a => a.plate === plate));
      })
      .catch(() => setVehicleAccidents([]));
  };

  // Fetches every real Document record for this vehicle (both the 5 fixed
  // types and any extras) — the fixed rows' `selectedVehicle.docs.*` values
  // are an embedded snapshot with no _id, so deleting one requires looking
  // up its actual Document record here.
  const refreshVehicleDocuments = (vehicleId, plate) => {
    apiEndpoints.documents.list()
      .then(res => {
        const mine = (res.data || []).filter(d => d.vehicleId === vehicleId || d.plate === plate);
        setVehicleAllDocs(mine);
      })
      .catch(() => setVehicleAllDocs([]));
  };

  const refreshVehicleEvents = (plate) => {
    apiEndpoints.events.list()
      .then(res => {
        setVehicleEvents((res.data || []).filter(e => e.plate === plate));
      })
      .catch(() => setVehicleEvents([]));
  };

  useEffect(() => {
    if (!selectedVehicle?.plate) {
      setVehicleCosts([]);
      setVehicleAccidents([]);
      setVehicleAllDocs([]);
      setVehicleEvents([]);
      setShowMoreDocs(false);
      return;
    }
    refreshVehicleCosts(selectedVehicle.plate);
    refreshVehicleAccidents(selectedVehicle.plate);
    refreshVehicleEvents(selectedVehicle.plate);
    refreshVehicleDocuments(selectedVehicle.id || selectedVehicle._id, selectedVehicle.plate);
  }, [selectedVehicle?.plate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);
        const res = await apiEndpoints.vehicles.list({ category });
        setVehicles(res.data);
        setFilteredVehicles(res.data);
        // Auto-open if navigated here from another page (e.g. Drivers'
        // "Current Vehicle" link) with a target vehicle id in location.state.
        const targetId = location.state?.selectedVehicleId;
        if (targetId) {
          const found = res.data.find((v) => v.id === targetId);
          if (found) setSelectedVehicle(found);
        }
      } catch (err) {
        console.error('Failed to load vehicles:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [category]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let next = vehicles;

    if (filter !== 'all') {
      next = next.filter((v) => v.status === filter);
    }

    if (companyFilter !== 'all') {
      next = next.filter((v) => v.company === companyFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      next = next.filter((v) => {
        return [v.plate, v.brand, v.model, v.company, v.status]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
    }

    next = [...next].sort((a, b) => {
      const minExp = (v) => {
        const exps = [v.docs?.rca?.exp, v.docs?.itp?.exp, v.docs?.casco?.exp, v.docs?.rovinieta?.exp, v.docs?.roadAssistance?.exp]
          .filter(Boolean)
          .map(e => new Date(e).getTime())
          .filter(t => !isNaN(t));
        return exps.length ? Math.min(...exps) : Infinity;
      };
      return minExp(a) - minExp(b);
    });

    setFilteredVehicles(next);
  }, [filter, companyFilter, searchTerm, vehicles]);

  const statusCounts = vehicles.reduce((acc, vehicle) => {
    acc[vehicle.status] = (acc[vehicle.status] || 0) + 1;
    return acc;
  }, {});

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
  };

  const handleBack = () => {
    setSelectedVehicle(null);
  };

  const refresh = async () => {
    const res = await apiEndpoints.vehicles.list({ category });
    setVehicles(res.data);
    setFilteredVehicles(res.data);
    return res.data;
  };

  const handleAddVehicle = () => {
    setEditingVehicle(null);
    setShowVehicleForm(true);
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setShowVehicleForm(true);
  };

  const handleSaveVehicle = async (vehicleData) => {
    try {
      if (editingVehicle) {
        await apiEndpoints.vehicles.update(editingVehicle.id, vehicleData);
      } else {
        await apiEndpoints.vehicles.create({ ...vehicleData, category });
      }
      const updated = await refresh();
      if (selectedVehicle) {
        const next = updated.find((v) => v.id === selectedVehicle.id);
        if (next) setSelectedVehicle(next);
      }
      setShowVehicleForm(false);
    } catch (err) {
      console.error('Failed to save vehicle:', err);
      alert('Error saving vehicle. Please try again.');
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!await confirmDialog('Are you sure you want to delete this vehicle?')) return;
    try {
      await apiEndpoints.vehicles.delete(vehicleId);
      setSelectedVehicle(null);
      await refresh();
    } catch (err) {
      console.error('Failed to delete vehicle:', err);
      alert('Error deleting vehicle. Please try again.');
    }
  };

  const handleVehicleAddEvent = () => { setEditingEvent(null); setShowEventForm(true); };

  const handleEditEvent = (event) => { setEditingEvent(event); setShowEventForm(true); };

  const handleDeleteEvent = async (event) => {
    if (!await confirmDialog('Delete this event? This cannot be undone.')) return;
    try {
      await apiEndpoints.events.delete(event._id);
      refreshVehicleEvents(selectedVehicle.plate);
    } catch (err) {
      console.error('Failed to delete event:', err);
      alert('Error deleting event. Please try again.');
    }
  };

  const handleSaveVehicleEvent = async (payload) => {
    try {
      if (editingEvent?._id) {
        await apiEndpoints.events.update(editingEvent._id, payload);
      } else {
        await apiEndpoints.events.create(payload);
      }
      setShowEventForm(false);
      refreshVehicleEvents(selectedVehicle.plate);
      const updated = await refresh();
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) setSelectedVehicle(next);
    } catch (err) {
      console.error('Failed to create event:', err);
      alert('Error saving event. Please try again.');
    }
  };

  const handleVehicleAddDocument = () => { setEditingDocument(null); setShowDocumentForm(true); };

  const handleVehicleRenewDocument = (type, embeddedDoc) => {
    setEditingDocument({
      _id: 'renew',
      plate: selectedVehicle.plate || '',
      documentType: type,
      issuer: embeddedDoc?.issuer || '',
      issueDate: embeddedDoc?.issued || '',
      expiryDate: embeddedDoc?.exp || '',
    });
    setShowDocumentForm(true);
  };

  const handleSaveVehicleDocument = async (payload, file) => {
    try {
      await apiEndpoints.documents.create(payload, file);
      setShowDocumentForm(false);
      const updated = await refresh();
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) {
        setSelectedVehicle(next);
        refreshVehicleDocuments(next.id || next._id, next.plate);
        refreshVehicleCosts(next.plate);
      }
    } catch (err) {
      console.error('Failed to save document:', err);
      alert('Error saving document. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc) => {
    if (!doc?._id) return;
    if (!await confirmDialog('Delete this document? This cannot be undone.')) return;
    try {
      await apiEndpoints.documents.delete(doc._id);
      const updated = await refresh();
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) {
        setSelectedVehicle(next);
        refreshVehicleDocuments(next.id || next._id, next.plate);
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Error deleting document. Please try again.');
    }
  };

  // Clears one of the 5 fixed document types (RCA/ITP/CASCO/Rovinieta/Road
  // Assist) in full — file, expiry date, everything — regardless of whether
  // it currently has any data, unlike handleDeleteDocument which needs a
  // real Document record to act on.
  const handleDeleteFixedDocument = async (docKey) => {
    if (!await confirmDialog('Delete this document? This cannot be undone.')) return;
    try {
      const vid = selectedVehicle.id || selectedVehicle._id;
      await apiEndpoints.vehicles.deleteDocType(vid, docKey);
      const updated = await refresh();
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) {
        setSelectedVehicle(next);
        refreshVehicleDocuments(next.id || next._id, next.plate);
      }
      // Rows with no data to begin with won't visibly change after deleting —
      // this confirms the action actually ran either way.
      setDocActionMsg('Document deleted.');
      setTimeout(() => setDocActionMsg(null), 2500);
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Error deleting document. Please try again.');
    }
  };

  const handleExportSingleVehicle = () => {
    const v = selectedVehicle;
    const headers = ['Plate', 'Brand', 'Model', 'Year', 'Fuel', 'Body Type', 'Color', 'Company', 'VIN', 'Driver', 'Status'];
    const row = [v.plate, v.brand, v.model, v.year, v.fuel, v.bodyType || v.type || '', v.color || '', v.company, v.vin || v.chassis || '', v.driverId || 'Available', v.status];
    const csv = [headers, row]
      .map((r) => r.map((val) => `"${String(val || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `vehicle_${v.plate || 'export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleArchiveVehicle = async () => {
    if (!await confirmDialog('Archive this vehicle? Its status will be set to Arhivat.')) return;
    try {
      await apiEndpoints.vehicles.update(selectedVehicle.id || selectedVehicle._id, { status: 'Arhivat' });
      setSelectedVehicle(null);
      await refresh();
    } catch (err) {
      console.error('Failed to archive vehicle:', err);
      alert('Error archiving vehicle. Please try again.');
    }
  };

  const handleOpenDriverModal = async () => {
    try {
      const res = await apiEndpoints.drivers.list();
      setDrivers(res.data || []);
      setShowDriverModal(true);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      alert('Error loading drivers. Please try again.');
    }
  };

  const handleAssignDriver = async (driverId) => {
    try {
      await apiEndpoints.assignments.create({ vehicleId: selectedVehicle.id || selectedVehicle._id, driverId });
      setShowDriverModal(false);
      const updated = await refresh();
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) setSelectedVehicle(next);
    } catch (err) {
      console.error('Failed to assign driver:', err);
      alert('Error assigning driver. Please try again.');
    }
  };

  const handleEditAccident = (accident) => { setEditingAccident(accident); setShowAccidentForm(true); };

  const handleDeleteAccident = async (accident) => {
    if (!await confirmDialog('Delete this accident record?')) return;
    try {
      await apiEndpoints.accidents.delete(accident._id);
      if (selectedVehicle?.plate) refreshVehicleAccidents(selectedVehicle.plate);
    } catch (err) {
      console.error('Failed to delete accident:', err);
      alert('Error deleting accident. Please try again.');
    }
  };

  const handleSaveAccident = async (payload, file) => {
    try {
      if (editingAccident?._id) {
        await apiEndpoints.accidents.update(editingAccident._id, payload, file);
      } else {
        await apiEndpoints.accidents.create(payload, file);
      }
      setShowAccidentForm(false);
      setEditingAccident(null);
      if (selectedVehicle?.plate) refreshVehicleAccidents(selectedVehicle.plate);
    } catch (err) {
      console.error('Failed to save accident:', err);
      alert('Error saving accident. Please try again.');
    }
  };

  const handleEditCost = (cost) => { setEditingCost(cost); setShowCostForm(true); };

  const handleDeleteCost = async (cost) => {
    if (!await confirmDialog('Delete this cost entry?')) return;
    try {
      await apiEndpoints.costs.delete(cost._id);
      if (selectedVehicle?.plate) refreshVehicleCosts(selectedVehicle.plate);
    } catch (err) {
      console.error('Failed to delete cost:', err);
      alert('Error deleting cost. Please try again.');
    }
  };

  const handleSaveCost = async (payload, file) => {
    try {
      if (editingCost?._id) {
        await apiEndpoints.costs.update(editingCost._id, payload, file);
      } else {
        await apiEndpoints.costs.create(payload, file);
      }
      setShowCostForm(false);
      setEditingCost(null);
      if (selectedVehicle?.plate) refreshVehicleCosts(selectedVehicle.plate);
    } catch (err) {
      console.error('Failed to save cost:', err);
      alert('Error saving cost. Please try again.');
    }
  };

  const handleDeallocate = async () => {
    if (!await confirmDialog('Remove the driver assignment from this vehicle?')) return;
    try {
      await apiEndpoints.assignments.create({ vehicleId: selectedVehicle.id || selectedVehicle._id, driverId: null });
      const updated = await refresh();
      const vid = selectedVehicle.id || selectedVehicle._id;
      const next = updated.find((v) => (v.id || v._id) === vid);
      if (next) setSelectedVehicle(next);
    } catch (err) {
      console.error('Failed to deallocate driver:', err);
      alert('Error deallocating driver. Please try again.');
    }
  };

  const refreshSelectedVehicle = async () => {
    const updated = await refresh();
    const vid = selectedVehicle.id || selectedVehicle._id;
    const next = updated.find((v) => (v.id || v._id) === vid);
    if (next) setSelectedVehicle(next);
    return next;
  };

  const handleSubAssign = async (data) => {
    try {
      const res = await apiEndpoints.vehicles.subAssign(selectedVehicle.id, data);
      setSelectedVehicle(res.data);
      await refresh();
    } catch (err) {
      console.error('Sub-assign failed:', err);
      throw err;
    }
  };

  const handleSubDeallocate = async () => {
    try {
      const res = await apiEndpoints.vehicles.subDeallocate(selectedVehicle.id, {
        returnDate:      subDeallocateForm.returnDate      || undefined,
        fuelLevelReturn: subDeallocateForm.fuelLevelReturn !== '' ? Number(subDeallocateForm.fuelLevelReturn) : undefined,
      });
      setSelectedVehicle(res.data);
      setShowSubDeallocate(false);
      setSubDeallocateForm({ returnDate: '', fuelLevelReturn: '' });
      await refresh();
    } catch (err) {
      console.error('Sub-deallocate failed:', err);
      alert('Error removing temporary driver. Please try again.');
    }
  };

  const handleExport = () => {
    const headers = ['Plate', 'Brand', 'Model', 'Year', 'Fuel', 'Company', 'Driver', 'Status', 'RCA Exp', 'RCA Status', 'ITP Exp', 'ITP Status', 'CASCO Exp', 'CASCO Status'];
    const rows = filteredVehicles.map((vehicle) => [
      vehicle.plate,
      vehicle.brand,
      vehicle.model,
      vehicle.year,
      vehicle.fuel,
      vehicle.company,
      vehicle.driverName || 'Available',
      vehicle.status,
      vehicle.docs?.rca?.exp || '',
      vehicle.docs?.rca?.status || '',
      vehicle.docs?.itp?.exp || '',
      vehicle.docs?.itp?.status || '',
      vehicle.docs?.casco?.exp || '',
      vehicle.docs?.casco?.status || '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'vehicles_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getVehicleStatusClass = (status) => {
    switch (status) {
      case 'Disponibil':
        return 'status-card-good';
      case 'Alocat':
        return 'status-card-warning';
      case 'Service':
        return 'status-card-critical';
      default:
        return '';
    }
  };

  if (loading) return <div className="page-loading">Loading vehicles...</div>;

  if (selectedVehicle) {
    const plate = selectedVehicle.plate || selectedVehicle.registration || 'N/A';
    const getInspFileUrl = (fn) => `${(process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '')}/uploads/inspections/${fn}`;
    const driverName = selectedVehicle.driverName || selectedVehicle.driver || 'Unassigned';
    const driverRole = selectedVehicle.driverRole || 'Fleet driver';
    const activeEvents = vehicleEvents;
    const rca           = selectedVehicle.docs?.rca;
    const itp           = selectedVehicle.docs?.itp;
    const casco         = selectedVehicle.docs?.casco;
    const rovinieta     = selectedVehicle.docs?.rovinieta;
    const roadAssistance = selectedVehicle.docs?.roadAssistance;
    const vehicleExtraDocs = vehicleAllDocs.filter((d) => !STANDARD_DOC_TYPES.includes((d.documentType || '').toUpperCase()));

    const docStatusColor = (doc) => {
      const s = doc?.status;
      if (!doc) return { color: '#9ca3af', bg: '#f9fafb', label: 'Not on file' };
      if (s === 'critical') return { color: '#ef4444', bg: '#fef2f2', label: 'Critical' };
      if (s === 'expiring') return { color: '#f59e0b', bg: '#fffbeb', label: 'Expiring soon' };
      return { color: '#10b981', bg: '#ecfdf5', label: 'Valid' };
    };

    return (
      <div>
        <button className="back-btn" onClick={handleBack}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to vehicles
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate">
            <span className="plate-pill">{plate}</span>
          </div>
          <div className="vprofile-model">{selectedVehicle.brand} {selectedVehicle.model}</div>
          <div className="vprofile-sub">{selectedVehicle.year} • {selectedVehicle.fuel || '—'} • {selectedVehicle.company || '—'}</div>
        </div>

        <div className="detail-grid">

          {/* ── Row 1: Vehicle Details | Quick Actions + Assigned Driver ── */}

          <div className="card">
            <div className="card-header">
              <div className="card-title">Vehicle Details</div>
              {isAdmin && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEditVehicle(selectedVehicle)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteVehicle(selectedVehicle.id)}>Delete</button>
                </div>
              )}
            </div>
            <div className="card-body">
              <div className="info-row"><div className="info-lbl">Registration number</div><div className="info-val">{plate}</div></div>
              <div className="info-row"><div className="info-lbl">Brand / Model</div><div className="info-val">{selectedVehicle.brand} {selectedVehicle.model}</div></div>
              <div className="info-row"><div className="info-lbl">Year of manufacture</div><div className="info-val">{selectedVehicle.year || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Fuel type</div><div className="info-val">{selectedVehicle.fuel || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Body type</div><div className="info-val">{selectedVehicle.bodyType || selectedVehicle.type || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Color</div><div className="info-val">{selectedVehicle.color || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Company</div><div className="info-val">{selectedVehicle.company || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">VIN / Chassis</div><div className="info-val">{selectedVehicle.vin || selectedVehicle.chassis || '—'}</div></div>
              <div className="info-row" style={{ borderBottom: 'none' }}><div className="info-lbl">Assigned Driver</div><div className="info-val">{selectedVehicle.driverName || '—'}</div></div>
            </div>
          </div>

          <div className="detail-col-group">
            <div className="card quick-actions-card">
              <div className="card-header"><div className="card-title">Quick Actions</div></div>
              <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleVehicleAddEvent}>Add Event</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setEditingAccident(null); setShowAccidentForm(true); }}>Report Accident</button>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowStatsModal(true)}>See Statistics</button>
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleExportSingleVehicle}>Export Data</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleVehicleAddDocument}>Add Documents</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleOpenDriverModal}>Allocate / Change Driver</button>}
                {isAdmin && <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => { setEditingCost(null); setShowCostForm(true); }}>Add Cost</button>}
                {isAdmin && <button className="btn btn-danger" style={{ width: '100%' }} onClick={handleArchiveVehicle}>Archive the Vehicle</button>}
              </div>
            </div>
            <div className="card assigned-driver-card">
              <div className="card-header">
                <div className="card-title">Assigned Driver</div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="small-link btn btn-ghost btn-xs" onClick={() => setShowDriverProfileModal(true)}>Profile →</button>
                  <button className="small-link btn btn-ghost btn-xs" onClick={async () => {
                    try { const r = await apiEndpoints.drivers.list(); setDrivers(r.data || []); } catch {}
                    setShowDriverHistoryModal(true);
                  }}>User Log →</button>
                </div>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div className="avatar-circle">{driverName.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}</div>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-1)' }}>{driverName}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>{driverRole} • {selectedVehicle.company || '—'}</div>
                  </div>
                </div>
                <div className="info-row" style={{ padding: '8px 0' }}>
                  <div className="info-lbl">Permissive</div>
                  <div className="info-val">{selectedVehicle.permis || selectedVehicle.permit || 'B'}</div>
                </div>
                <div className="info-row" style={{ padding: '8px 0', borderBottom: 'none' }}>
                  <div className="info-lbl">Permit expires</div>
                  <div className="info-val">{selectedVehicle.permitExpires || selectedVehicle.permitExpiry || '—'}</div>
                </div>
                {isAdmin && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                    <button className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={handleOpenDriverModal}>Change driver</button>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={handleDeallocate}>Deallocate</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Row 2: Acts & Documents | Take Inspection + Temporary Driver ── */}

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                Acts & Documents
                {docActionMsg && (
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#10b981', background: '#ecfdf5', padding: '3px 10px', borderRadius: 9999 }}>
                    ✓ {docActionMsg}
                  </span>
                )}
              </div>
              {isAdmin && <button className="btn btn-primary btn-sm" onClick={handleVehicleAddDocument}>+ Add</button>}
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              {[{ label: 'RCA', sublabel: 'Compulsory Insurance', doc: rca, type: 'RCA', docKey: 'rca' }, { label: 'ITP', sublabel: 'Technical Inspection', doc: itp, type: 'ITP', docKey: 'itp' }, { label: 'Rovinieta', sublabel: 'Road Fee', doc: rovinieta, type: 'Rovinieta', docKey: 'rovinieta' }, { label: 'CASCO', sublabel: 'Optional Insurance', doc: casco, type: 'CASCO', docKey: 'casco' }, { label: 'Road Assist', sublabel: 'Road Assistance', doc: roadAssistance, type: 'Road Assistance', docKey: 'roadAssistance' }].map((item, idx, arr) => {
                const hasFile = !!item.doc?.fileUrl;
                const docFilenames = item.doc?.fileUrls?.length ? item.doc.fileUrls : (hasFile ? [item.doc.fileUrl] : []);
                const fileUrls = hasFile ? (() => {
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
                  return docFilenames.map((name) => `${apiUrl}/static/${name}`);
                })() : [];
                const isLast = idx === arr.length - 1;
                let daysLeft = null, daysColor = '#10b981', daysLabel = null;
                if (item.doc?.exp) {
                  const expStr = String(item.doc.exp);
                  const m = expStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
                  const expDate = m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(expStr);
                  if (!isNaN(expDate)) {
                    daysLeft = Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24));
                    daysColor = daysLeft < 7 ? '#ef4444' : daysLeft < 30 ? '#f59e0b' : '#10b981';
                    daysLabel = daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`;
                  }
                }
                const hoverData = {
                  label: item.label, sublabel: item.sublabel, hasFile,
                  issuer: item.doc?.issuer, issued: item.doc?.issued, exp: item.doc?.exp,
                  documentNumber: item.doc?.number,
                  daysLeft, daysColor, daysLabel,
                  thumbUrl: fileUrls[0], isImageFile: isImageFilename(docFilenames[0]), fileCount: docFilenames.length,
                };
                return (
                  <div
                    key={item.label}
                    onMouseEnter={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev?.pinned ? prev : { point, data: hoverData, key: item.label, pinned: false })); }}
                    onMouseMove={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev && !prev.pinned && prev.key === item.label ? { ...prev, point } : prev)); }}
                    onMouseLeave={() => setDocHover((prev) => (prev?.pinned ? prev : null))}
                    onClick={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev?.pinned && prev.key === item.label ? null : { point, data: hoverData, key: item.label, pinned: true })); }}
                    style={{ padding: '10px 16px', borderBottom: isLast ? 'none' : '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                  >
                    <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 8, background: hasFile ? '#eff6ff' : '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: hasFile ? '#3b82f6' : '#ef4444', letterSpacing: '0.03em' }}>{item.label}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label} — {item.sublabel}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{hasFile ? (item.doc.issuer && item.doc.issuer !== '—' ? item.doc.issuer : 'On file') : 'No file attached'}{item.doc?.exp ? ` • Exp: ${item.doc.exp}` : ''}</span>
                        {daysLabel && <span style={{ padding: '1px 7px', borderRadius: 9999, fontSize: 11, fontWeight: 700, color: daysColor, background: `${daysColor}18`, whiteSpace: 'nowrap' }}>{daysLabel}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      {hasFile ? (
                        <button className="btn btn-ghost btn-xs" style={{ color: '#0077b6', padding: '4px 10px' }} onClick={() => setDocPreview({ urls: fileUrls, filenames: docFilenames, label: item.label })}>Preview</button>
                      ) : (
                        <span style={{ fontSize: 11, color: '#d1d5db', padding: '4px 6px' }}>No file</span>
                      )}
                      {isAdmin && <button className="btn btn-ghost btn-xs" style={{ color: '#059669', padding: '4px 10px' }} onClick={() => handleVehicleRenewDocument(item.type, item.doc)}>Edit</button>}
                      {isAdmin && <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', padding: '4px 10px' }} onClick={() => handleDeleteFixedDocument(item.docKey)}>Delete</button>}
                    </div>
                  </div>
                );
              })}
              {showMoreDocs && vehicleExtraDocs.map((doc) => {
                const hasFile = !!doc.fileUrl;
                const docFilenames = doc.fileUrls?.length ? doc.fileUrls : (hasFile ? [doc.fileUrl] : []);
                const fileUrls = hasFile ? (() => {
                  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
                  return docFilenames.map((name) => `${apiUrl}/static/${name}`);
                })() : [];
                const expStr = String(doc.expiryDate || '');
                const expDate = (() => { const m = expStr.match(/^(\d{2})\.(\d{2})\.(\d{4})$/); return m ? new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`) : new Date(expStr); })();
                let daysLabel = null, daysColor = '#10b981', daysLeft = null;
                if (!isNaN(expDate)) { daysLeft = Math.ceil((expDate - new Date()) / 86400000); daysColor = daysLeft < 7 ? '#ef4444' : daysLeft < 30 ? '#f59e0b' : '#10b981'; daysLabel = daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`; }
                const hoverData = {
                  label: prettyDocumentType(doc.documentType), sublabel: 'Additional Document', hasFile,
                  issuer: doc.issuer, issued: doc.issueDate, exp: doc.expiryDate,
                  documentNumber: doc.documentNumber, notes: doc.notes,
                  daysLeft, daysColor, daysLabel,
                  thumbUrl: fileUrls[0], isImageFile: isImageFilename(docFilenames[0]), fileCount: docFilenames.length,
                };
                return (
                  <div
                    key={doc._id}
                    onMouseEnter={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev?.pinned ? prev : { point, data: hoverData, key: doc._id, pinned: false })); }}
                    onMouseMove={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev && !prev.pinned && prev.key === doc._id ? { ...prev, point } : prev)); }}
                    onMouseLeave={() => setDocHover((prev) => (prev?.pinned ? prev : null))}
                    onClick={(e) => { const point = { x: e.clientX, y: e.clientY }; setDocHover((prev) => (prev?.pinned && prev.key === doc._id ? null : { point, data: hoverData, key: doc._id, pinned: true })); }}
                    style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                  >
                    <div style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 8, background: hasFile ? '#f0fdf4' : '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontSize: 9, fontWeight: 800, color: hasFile ? '#059669' : '#ef4444', letterSpacing: '0.02em', textAlign: 'center' }}>{(prettyDocumentType(doc.documentType) || 'DOC').slice(0, 5)}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prettyDocumentType(doc.documentType)}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span>{doc.issuer && doc.issuer !== '—' ? doc.issuer : 'On file'}{doc.expiryDate ? ` • Exp: ${expStr.slice(0, 10)}` : ''}</span>
                        {daysLabel && <span style={{ padding: '1px 7px', borderRadius: 9999, fontSize: 11, fontWeight: 700, color: daysColor, background: `${daysColor}18`, whiteSpace: 'nowrap' }}>{daysLabel}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                      {hasFile ? <button className="btn btn-ghost btn-xs" style={{ color: '#0077b6', padding: '4px 10px' }} onClick={() => setDocPreview({ urls: fileUrls, filenames: docFilenames, label: prettyDocumentType(doc.documentType) })}>Preview</button> : <span style={{ fontSize: 11, color: '#d1d5db', padding: '4px 6px' }}>No file</span>}
                      {isAdmin && <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', padding: '4px 10px' }} onClick={() => handleDeleteDocument(doc)}>Delete</button>}
                    </div>
                  </div>
                );
              })}
              {vehicleExtraDocs.length > 0 && (
                <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', textAlign: 'center' }}>
                  <button className="btn btn-ghost btn-xs" style={{ color: '#0077b6', fontSize: 12, width: '100%' }} onClick={() => setShowMoreDocs(v => !v)}>
                    {showMoreDocs ? 'Show Less ▲' : `Show More (${vehicleExtraDocs.length} more document${vehicleExtraDocs.length > 1 ? 's' : ''}) ▼`}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="detail-col-group">
            <div className="card">
              <div className="card-header"><div className="card-title">Take Inspection</div></div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setInspectionType('checkin')}>Check In</button>
                <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => setInspectionType('checkout')}>Check Out</button>
                <button className="btn btn-ghost" style={{ width: '100%' }} onClick={async () => {
                  try {
                    const res = await apiEndpoints.inspections.list({ vehicleId: selectedVehicle.id });
                    setInspectionRecords(res.data || []);
                  } catch { setInspectionRecords([]); }
                  setShowInspectionDetails(true);
                }}>See Details</button>
              </div>
            </div>
            {(() => {
              const sub = selectedVehicle.subAssignment;
              const subName = sub?.driverName || 'Unassigned';
              const subInitials = subName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div className="temp-driver-section" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-xl)', boxShadow: 'var(--sh-sm)', overflow: 'hidden' }}>
                  <div className="card-header">
                    <div className="card-title">Temporary Driver</div>
                    {(sub?.driverId || (selectedVehicle.subDriverLog || []).length > 0) && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {sub?.driverId && <button className="small-link btn btn-ghost btn-xs" onClick={() => setShowSubProfile(true)}>Profile →</button>}
                        <button className="small-link btn btn-ghost btn-xs" onClick={() => setShowSubLog(true)}>User Log →</button>
                      </div>
                    )}
                  </div>
                  <div className="card-body">
                    {sub?.driverId ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                          <div className="avatar-circle" style={{ background: '#f59e0b' }}>{subInitials}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>{subName}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{sub.driverRole || 'Temporary Driver'}</div>
                          </div>
                          <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 10, fontWeight: 700, color: '#d97706', background: '#fef3c7', flexShrink: 0 }}>TEMP</span>
                        </div>
                        <div style={{ overflow: 'hidden' }}>
                          {sub.from && <div className="info-row" style={{ padding: '6px 0' }}><div className="info-lbl">From</div><div className="info-val">{sub.from}</div></div>}
                          {sub.to   && <div className="info-row" style={{ padding: '6px 0' }}><div className="info-lbl">Until</div><div className="info-val">{sub.to}</div></div>}
                          {sub.reason && <div className="info-row" style={{ padding: '6px 0', borderBottom: 'none' }}><div className="info-lbl">Reason</div><div className="info-val" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.reason}</div></div>}
                        </div>
                        {isAdmin && (
                          <div style={{ marginTop: 16 }}>
                            <button className="btn btn-secondary btn-sm" style={{ width: '100%' }} onClick={async () => {
                              try { const r = await apiEndpoints.drivers.list(); setDrivers(r.data || []); } catch {}
                              setShowSubAssignForm(true);
                            }}>Change driver</button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ color: 'var(--text-3)', fontSize: 13, marginBottom: 14 }}>No temporary driver assigned.</div>
                        {isAdmin && (
                          <button className="btn btn-primary btn-sm" onClick={async () => {
                            try { const r = await apiEndpoints.drivers.list(); setDrivers(r.data || []); } catch {}
                            setShowSubAssignForm(true);
                          }}>+ Assign Temporary Driver</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Row 3: Active Events | Accidents (same bottom edge) ── */}

          <div className="card">
            <div className="card-header">
              <div className="card-title">Active Events</div>
              <button className="btn btn-ghost btn-sm" onClick={handleVehicleAddEvent}>+ Event</button>
            </div>
            <div className="card-body">
              {activeEvents.length > 0 ? activeEvents.map((event, i) => (
                <div key={event._id || event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', padding: '10px 0', borderBottom: i < activeEvents.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.evt || event.name || 'Event'}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{event.plate || plate}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', paddingTop: '2px' }}>{event.created || event.startDate || '—'}</div>
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-xs" style={{ color: '#059669', padding: '2px 8px', fontSize: 11 }} onClick={() => handleEditEvent(event)}>Edit</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', padding: '2px 8px', fontSize: 11 }} onClick={() => handleDeleteEvent(event)}>Delete</button>
                      </div>
                    )}
                  </div>
                </div>
              )) : (
                <div style={{ padding: '12px 0', color: 'var(--text-3)', fontSize: '13px' }}>No active events.</div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Accidents</div>
              <button className="btn btn-primary btn-sm" onClick={() => { setEditingAccident(null); setShowAccidentForm(true); }}>+ Report</button>
            </div>
            <div className="card-body">
              {vehicleAccidents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', padding: '12px 0', fontSize: '13px' }}>No accidents recorded.</div>
              ) : (
                vehicleAccidents.slice(0, 5).map((a, i) => (
                  <div key={a._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < Math.min(vehicleAccidents.length, 5) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: a.damageLevel === 'total_loss' ? '#7c3aed' : a.damageLevel === 'major' ? '#ef4444' : '#f59e0b' }}>
                        {a.damageLevel === 'total_loss' ? 'Total Loss' : a.damageLevel === 'major' ? 'Major' : 'Minor'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>
                        {a.date ? new Date(a.date).toLocaleDateString() : '—'}{a.location ? ` • ${a.location}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      {a.repairCost ? <span style={{ fontSize: '13px', fontWeight: 700, color: '#0077b6' }}>{Number(a.repairCost).toLocaleString()} {a.currency || 'RON'}</span> : <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>}
                      {getAttachmentsList(a).length > 0 && (
                        <button
                          className="btn btn-ghost btn-xs"
                          style={{ color: '#0077b6', padding: '2px 8px', fontSize: 11 }}
                          onClick={() => setDocPreview({ ...getAttachmentsPreview(a, 'accidents'), label: 'Accident Attachment' })}
                        >
                          Preview
                        </button>
                      )}
                      {isAdmin && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-ghost btn-xs" style={{ color: '#059669', padding: '2px 8px', fontSize: 11 }} onClick={() => handleEditAccident(a)}>Edit</button>
                          <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444', padding: '2px 8px', fontSize: 11 }} onClick={() => handleDeleteAccident(a)}>Delete</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              {vehicleAccidents.length > 5 && (
                <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{vehicleAccidents.length - 5} more</div>
              )}
            </div>
          </div>

          {/* ── Row 4: Costs + Company (left only) ── */}

          <div className="detail-col-group">
            <CostsCard
              costs={vehicleCosts}
              onAdd={() => { setEditingCost(null); setShowCostForm(true); }}
              onPreview={(cost) => setDocPreview({ ...getAttachmentsPreview(cost, 'costs'), label: 'Cost Receipt' })}
              onEdit={handleEditCost}
              onDelete={handleDeleteCost}
              isAdmin={isAdmin}
            />
            <div className="card">
              <div className="card-header"><div className="card-title">Company</div></div>
              <div className="card-body">
                {selectedVehicle.company ? (
                  <>
                    <div className="info-row"><div className="info-lbl">Company name</div><div className="info-val" style={{ fontWeight: 700 }}>{selectedVehicle.company}</div></div>
                    <div className="info-row"><div className="info-lbl">Fleet vehicles</div><div className="info-val">{vehicles.filter(v => v.company === selectedVehicle.company).length} vehicle(s)</div></div>
                    <div className="info-row" style={{ borderBottom: 'none' }}><div className="info-lbl">Active in company</div><div className="info-val">{vehicles.filter(v => v.company === selectedVehicle.company && v.status === 'Alocat').length} assigned</div></div>
                  </>
                ) : (
                  <div style={{ color: 'var(--text-3)', padding: '12px 0' }}>No company assigned.</div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Event Form */}
        <EventForm
          isOpen={showEventForm}
          onClose={() => { setShowEventForm(false); setEditingEvent(null); }}
          event={editingEvent || { plate, car: `${selectedVehicle.brand} ${selectedVehicle.model}` }}
          onSave={handleSaveVehicleEvent}
        />

        {/* Document Form */}
        <DocumentForm
          isOpen={showDocumentForm}
          onClose={() => setShowDocumentForm(false)}
          document={editingDocument || { plate }}
          onSave={handleSaveVehicleDocument}
        />

        {/* Vehicle Edit Form */}
        <VehicleForm
          isOpen={showVehicleForm}
          onClose={() => setShowVehicleForm(false)}
          vehicle={editingVehicle}
          onSave={handleSaveVehicle}
          isTemporary={category === 'temporary'}
        />

        {/* Accident Form */}
        <AccidentForm
          isOpen={showAccidentForm}
          onClose={() => { setShowAccidentForm(false); setEditingAccident(null); }}
          accident={editingAccident}
          defaultPlate={plate}
          defaultCompany={selectedVehicle.company || ''}
          defaultVehicleName={`${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim()}
          onSave={handleSaveAccident}
        />

        {/* Cost Form */}
        <CostForm
          isOpen={showCostForm}
          onClose={() => { setShowCostForm(false); setEditingCost(null); }}
          cost={editingCost || { plate, company: selectedVehicle.company || '', vehicleName: `${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim() }}
          onSave={handleSaveCost}
        />

        {/* Inspection Form */}
        <InspectionForm
          isOpen={!!inspectionType}
          onClose={() => setInspectionType(null)}
          type={inspectionType}
          vehicle={selectedVehicle}
          onSaved={async (formData, files) => {
            await apiEndpoints.inspections.create(
              { vehicleId: selectedVehicle.id, plate, type: inspectionType, ...formData },
              files
            );
          }}
        />

        {/* Inspection Details Modal */}
        <Modal isOpen={showInspectionDetails} onClose={() => { setShowInspectionDetails(false); setInspectionPreview(null); }} title="Inspection Details" size="lg">
          {(() => {
            const checkIns  = inspectionRecords.filter((r) => r.type === 'checkin');
            const checkOuts = inspectionRecords.filter((r) => r.type === 'checkout');
            const latestIn  = checkIns[0]  || null;
            const latestOut = checkOuts[0] || null;

            const DAMAGE_LABELS = [
              ['damageTop',    'Damage Top'],
              ['damageBottom', 'Damage Bottom'],
              ['damageFront',  'Damage Front'],
              ['damageBack',   'Damage Back'],
              ['damageSides',  'Damage from Sides'],
            ];

            const renderSection = (record, heading, accentColor) => (
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 4, height: 20, borderRadius: 2, background: accentColor }} />
                  <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)' }}>{heading}</span>
                  {record && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 'auto' }}>
                      {new Date(record.createdAt).toLocaleString()}
                    </span>
                  )}
                </div>

                {!record ? (
                  <div style={{ padding: '18px 16px', background: 'var(--surface-2, #f8fafc)', borderRadius: 10, color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>
                    No record available for this vehicle.
                  </div>
                ) : record.nothingToReport ? (
                  <div style={{ padding: '18px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, color: '#16a34a', fontSize: 13, textAlign: 'center', fontWeight: 600 }}>
                    Nothing to report — no damage or issues noted.
                  </div>
                ) : (
                  <div style={{ background: 'var(--surface-2, #f8fafc)', borderRadius: 10, overflow: 'hidden' }}>
                    {DAMAGE_LABELS.map(([key, label]) => (
                      <div key={key} className="info-row" style={{ padding: '9px 14px' }}>
                        <div className="info-lbl">{label}</div>
                        <div className="info-val">{record[key] || <span style={{ color: 'var(--text-3)' }}>—</span>}</div>
                      </div>
                    ))}
                    {record.notes && (
                      <div className="info-row" style={{ padding: '9px 14px' }}>
                        <div className="info-lbl">Notes</div>
                        <div className="info-val">{record.notes}</div>
                      </div>
                    )}
                    {record.files && record.files.length > 0 && (
                      <div style={{ padding: '10px 14px', borderTop: '1px solid #f3f4f6' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginBottom: 8, letterSpacing: '0.05em' }}>PROOF FILES</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {record.files.map((f, fi) => (
                            <button key={fi} type="button"
                              onClick={() => setInspectionPreview({ url: getInspFileUrl(f.filename), filename: f.originalName || f.filename })}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#0077b6', fontWeight: 500, maxWidth: 220, overflow: 'hidden' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                              </svg>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.originalName || f.filename}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );

            return (
              <div>
                {renderSection(latestIn,  'Check In',  '#10b981')}
                <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0 20px' }} />
                {renderSection(latestOut, 'Check Out', '#f59e0b')}
              </div>
            );
          })()}
        </Modal>
        <DocViewer url={inspectionPreview?.url} filename={inspectionPreview?.filename} onClose={() => setInspectionPreview(null)} />

        {/* Statistics Modal */}
        <Modal isOpen={showStatsModal} onClose={() => setShowStatsModal(false)} title="Vehicle Statistics" size="md">
          <VehicleStatsContent vehicle={selectedVehicle} />
        </Modal>

        {/* Driver Picker Modal */}
        <Modal isOpen={showDriverModal} onClose={() => setShowDriverModal(false)} title="Select Driver" size="md">
          <div>
            {drivers.length === 0 ? (
              <p style={{ color: 'var(--text-3)', textAlign: 'center', padding: '20px 0' }}>No drivers available.</p>
            ) : (
              <div style={{ display: 'grid', gap: '8px' }}>
                {drivers.map((driver) => (
                  <button
                    key={driver._id || driver.id}
                    className="btn btn-secondary"
                    style={{ width: '100%', textAlign: 'left', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}
                    onClick={() => handleAssignDriver(driver.id || driver._id)}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#3b82f6', color: 'white', fontSize: '13px', fontWeight: '600', flexShrink: 0 }}>
                      {(driver.name || 'D').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--text-1)' }}>{driver.name || 'Unknown'}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>{driver.phone || driver.email || driver.license || ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Modal>

        {/* Driver History / User Log Modal */}
        <Modal isOpen={showDriverHistoryModal} onClose={() => setShowDriverHistoryModal(false)} title="User Log" size="lg">
          {(() => {
            const vehicleId = selectedVehicle._id || selectedVehicle.id;
            const driverLog = selectedVehicle.driverLog || [];
            const allEntries = [];
            drivers.forEach((d) => {
              if (!d.history) return;
              d.history.forEach((h) => {
                if (String(h.vehicleId) === String(vehicleId) || h.plate === selectedVehicle.plate) {
                  allEntries.push({ ...h, driverName: d.name, driverRole: d.role, driverStringId: d.id });
                }
              });
            });
            allEntries.sort((a, b) => new Date(b.assignedAt) - new Date(a.assignedAt));

            // Find best-matching driverLog entry for a history row.
            // Match by driverId first; fall back to driverName for older records
            // where the ID format may differ.
            const findLogEntry = (e) => {
              let candidates = e.driverStringId
                ? driverLog.filter((l) => l.driverId === e.driverStringId)
                : [];
              if (candidates.length === 0 && e.driverName)
                candidates = driverLog.filter((l) => l.driverName === e.driverName);
              if (candidates.length === 0) return null;
              if (candidates.length === 1) return candidates[0];
              // Multiple entries for same driver — pick closest by assignedAt
              const histTime = e.assignedAt ? new Date(e.assignedAt).getTime() : 0;
              return candidates.reduce((best, l) => {
                const lt = l.assignedAt ? new Date(l.assignedAt).getTime() : 0;
                const bt = best.assignedAt ? new Date(best.assignedAt).getTime() : 0;
                return Math.abs(lt - histTime) < Math.abs(bt - histTime) ? l : best;
              });
            };

            // Exactly one entry is CURRENT: the most recent open entry that belongs
            // to the vehicle's current driver. Match by driverName first (always
            // reliable), fall back to driverId. Duplicate open entries from before
            // the driverChanged fix are treated as PAST.
            const currentIdx = allEntries.findIndex((e) => {
              if (e.unassignedAt) return false;
              if (selectedVehicle.driverName && e.driverName === selectedVehicle.driverName) return true;
              if (selectedVehicle.driverId && e.driverStringId === selectedVehicle.driverId) return true;
              return false;
            });

            const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';
            return (
              <div>
                {allEntries.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-3)', fontSize: '14px' }}>No driver history recorded for this vehicle.</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: 760 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                        {['DRIVER', 'ROLE', 'TAKEN DATE', 'RETURN DATE', 'FUEL TAKEN', 'FUEL RETURN', 'DURATION', 'STATUS'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allEntries.map((e, i) => {
                        const isCurrent = i === currentIdx;
                        const log = findLogEntry(e);
                        const start = e.assignedAt ? new Date(e.assignedAt) : null;
                        const end = isCurrent ? new Date() : (e.unassignedAt ? new Date(e.unassignedAt) : null);
                        const days = (start && end) ? Math.round((end - start) / 86400000) : null;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: isCurrent ? '#f0fdf4' : 'transparent' }}>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{e.driverName || '—'}</div>
                              <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{e.driverRole || ''}</div>
                            </td>
                            <td style={{ padding: '10px', color: 'var(--text-3)', fontSize: '12px' }}>{e.driverRole || '—'}</td>
                            <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{log?.assignedDate || '—'}</td>
                            <td style={{ padding: '10px', whiteSpace: 'nowrap', color: (isCurrent && !log?.returnDate) ? '#10b981' : 'var(--text-2)' }}>
                              {log?.returnDate || (isCurrent ? 'In use' : '—')}
                            </td>
                            <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>
                              {log?.fuelLevelTaken != null ? `${log.fuelLevelTaken}%` : '—'}
                            </td>
                            <td style={{ padding: '10px', whiteSpace: 'nowrap', color: (isCurrent && log?.fuelLevelReturn == null) ? '#10b981' : 'var(--text-2)' }}>
                              {log?.fuelLevelReturn != null ? `${log.fuelLevelReturn}%` : (isCurrent ? 'In use' : '—')}
                            </td>
                            <td style={{ padding: '10px', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{days !== null ? `${days}d` : '—'}</td>
                            <td style={{ padding: '10px' }}>
                              {isCurrent ? (
                                <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: '#10b981', background: '#d1fae5' }}>CURRENT</span>
                              ) : (
                                <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, color: '#6b7280', background: '#f3f4f6' }}>PAST</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal>

        {/* Driver Profile Modal */}
        <Modal isOpen={showDriverProfileModal} onClose={() => setShowDriverProfileModal(false)} title="Driver Profile" size="sm">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div className="avatar-circle" style={{ width: '52px', height: '52px', fontSize: '18px', flexShrink: 0 }}>
                {driverName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-1)' }}>{driverName}</div>
                <div style={{ fontSize: '13px', color: 'var(--text-3)' }}>{driverRole}</div>
              </div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Company</div>
              <div className="info-val">{selectedVehicle.company || '—'}</div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Assigned vehicle</div>
              <div className="info-val"><span className="plate-pill" style={{ fontSize: '12px' }}>{plate}</span></div>
            </div>
            <div className="info-row">
              <div className="info-lbl">Permit type</div>
              <div className="info-val">{selectedVehicle.permis || selectedVehicle.permit || 'B'}</div>
            </div>
            <div className="info-row" style={{ borderBottom: 'none' }}>
              <div className="info-lbl">Permit expires</div>
              <div className="info-val">{selectedVehicle.permitExpires || selectedVehicle.permitExpiry || '—'}</div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '8px' }}>
              <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => { setShowDriverProfileModal(false); handleOpenDriverModal(); }}>Change Driver</button>
              <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => { setShowDriverProfileModal(false); handleDeallocate(); }}>Deallocate</button>
            </div>
          </div>
        </Modal>

        {/* Sub Assign Form */}
        <SubAssignForm
          isOpen={showSubAssignForm}
          onClose={() => setShowSubAssignForm(false)}
          drivers={drivers}
          onSaved={handleSubAssign}
        />

        {/* Sub Driver Profile Modal */}
        {(() => {
          const sub = selectedVehicle.subAssignment;
          if (!sub) return null;
          const subName = sub.driverName || 'Temporary Driver';
          const subInitials = subName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
          return (
            <Modal isOpen={showSubProfile} onClose={() => setShowSubProfile(false)} title="Temporary Driver Profile" size="sm">
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                  <div className="avatar-circle" style={{ width: 52, height: 52, fontSize: 18, flexShrink: 0, background: '#f59e0b' }}>{subInitials}</div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)' }}>{subName}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{sub.driverRole || 'Temporary Driver'}</div>
                  </div>
                </div>
                {sub.from   && <div className="info-row"><div className="info-lbl">From</div><div className="info-val">{sub.from}</div></div>}
                {sub.to     && <div className="info-row"><div className="info-lbl">Until</div><div className="info-val">{sub.to}</div></div>}
                {sub.reason && <div className="info-row"><div className="info-lbl">Reason</div><div className="info-val">{sub.reason}</div></div>}
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <div className="info-lbl">Vehicle</div>
                  <div className="info-val"><span className="plate">{plate}</span></div>
                </div>
              </div>
            </Modal>
          );
        })()}

        {/* Sub Driver User Log Modal */}
        <Modal isOpen={showSubLog} onClose={() => setShowSubLog(false)} title="Temporary Driver Log" size="lg">
          {(() => {
            const log = selectedVehicle.subDriverLog || [];
            const activeSub = selectedVehicle.subAssignment;
            if (log.length === 0) return (
              <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px 0', fontSize: 13 }}>No temporary driver history recorded yet.</div>
            );
            return (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 680 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      {['DRIVER', 'FROM DATE', 'FUEL TAKEN', 'REASON', 'STATUS'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#9ca3af', fontWeight: 600, fontSize: 11, letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((entry, i) => {
                      const isCurrent = !entry.unassignedAt && !!activeSub?.driverId;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: isCurrent ? '#fffbeb' : 'transparent' }}>
                          <td style={{ padding: '10px' }}>
                            <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{entry.driverName || '—'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{entry.driverRole || ''}</div>
                          </td>
                          <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{entry.from || '—'}</td>
                          <td style={{ padding: '10px', whiteSpace: 'nowrap' }}>{entry.fuelLevelTaken != null ? `${entry.fuelLevelTaken}%` : '—'}</td>
                          <td style={{ padding: '10px', color: 'var(--text-3)', fontSize: 12 }}>{entry.reason || '—'}</td>
                          <td style={{ padding: '10px' }}>
                            {isCurrent
                              ? <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7' }}>ACTIVE</span>
                              : <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6' }}>PAST</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </Modal>

        {/* Sub Deallocate Modal */}
        <Modal isOpen={showSubDeallocate} onClose={() => setShowSubDeallocate(false)} title="Remove Temporary Driver" size="sm">
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 18 }}>Optionally record return details before removing the temporary driver.</p>
            <div className="form-group">
              <label className="form-label">Return Date</label>
              <input type="date" className="form-input" value={subDeallocateForm.returnDate}
                onChange={(e) => setSubDeallocateForm((p) => ({ ...p, returnDate: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Fuel Level Return (%)</label>
              <input type="number" className="form-input" value={subDeallocateForm.fuelLevelReturn} min="0" max="100"
                onChange={(e) => setSubDeallocateForm((p) => ({ ...p, fuelLevelReturn: e.target.value }))} placeholder="e.g. 60" />
            </div>
            <div className="form-actions">
              <button className="btn btn-ghost" onClick={() => setShowSubDeallocate(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleSubDeallocate}>Remove Temporary Driver</button>
            </div>
          </div>
        </Modal>

        {/* User Log Modal */}
        <Modal isOpen={showUserLogModal} onClose={() => setShowUserLogModal(false)} title="User Log" size="md">
          <div>
            {(!selectedVehicle.driverLog || selectedVehicle.driverLog.length === 0) ? (
              <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '24px 0', fontSize: '13px' }}>
                No driver history recorded yet. History is tracked from the next assignment.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {selectedVehicle.driverLog.map((entry, i) => {
                  const isActive = !entry.unassignedAt;
                  const initials = (entry.driverName || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
                  return (
                    <div key={i} style={{ display: 'flex', gap: 14, paddingBottom: i < selectedVehicle.driverLog.length - 1 ? 20 : 0 }}>
                      {/* Timeline line */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                        <div className="avatar-circle" style={{ width: 38, height: 38, fontSize: 13, flexShrink: 0, background: isActive ? '#0077b6' : '#94a3b8' }}>
                          {initials}
                        </div>
                        {i < selectedVehicle.driverLog.length - 1 && (
                          <div style={{ width: 2, flex: 1, background: '#e5e7eb', marginTop: 4 }} />
                        )}
                      </div>
                      {/* Entry content */}
                      <div style={{ flex: 1, paddingBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-1)' }}>{entry.driverName || '—'}</span>
                          {isActive && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#10b981', background: '#ecfdf5', padding: '2px 7px', borderRadius: 9999 }}>CURRENT</span>
                          )}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12 }}>
                          <div><span style={{ color: 'var(--text-3)' }}>Taken: </span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{entry.assignedDate || '—'}</span></div>
                          <div><span style={{ color: 'var(--text-3)' }}>Returned: </span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{entry.returnDate || (isActive ? 'In use' : '—')}</span></div>
                          <div><span style={{ color: 'var(--text-3)' }}>Fuel taken: </span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{entry.fuelLevelTaken != null ? `${entry.fuelLevelTaken}%` : '—'}</span></div>
                          <div><span style={{ color: 'var(--text-3)' }}>Fuel return: </span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{entry.fuelLevelReturn != null ? `${entry.fuelLevelReturn}%` : (isActive ? 'In use' : '—')}</span></div>
                        </div>
                        {entry.assignedAt && (
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                            Assigned {new Date(entry.assignedAt).toLocaleDateString()}
                            {entry.unassignedAt ? ` — Returned ${new Date(entry.unassignedAt).toLocaleDateString()}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>

        <DocViewer urls={docPreview?.urls} filenames={docPreview?.filenames?.length ? docPreview.filenames : [docPreview?.label]} onClose={() => setDocPreview(null)} />
        {docHover && (
          <HoverPopover point={docHover.point} pinned={docHover.pinned} onClose={() => setDocHover(null)}>
            <DocHoverContent {...docHover.data} />
          </HoverPopover>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">{category === 'temporary' ? 'Temporary motor vehicles' : 'Permanent motor vehicles'}</div>
          <div className="ph-sub">{vehicles.length} {category === 'temporary' ? 'temporary' : 'permanent'} vehicles registered in the fleet</div>
        </div>
        <div className="ph-actions">
          {isAdmin && <button className="btn btn-secondary btn-sm" onClick={handleExport}>
            <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4 4h12v2H4V4zm0 5h8v2H4V9zm0 5h12v2H4v-2zm12-9l-4 4h3v4h2v-4h3l-4-4z" />
            </svg>
            Export
          </button>}
          {isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={handleAddVehicle}>
              <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              New Vehicle
            </button>
          )}
        </div>
      </div>

      <div className="table-filters">
        <div className="filter-row">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="8.5" cy="8.5" r="5.5" />
              <path d="M13.5 13.5L18 18" />
            </svg>
            <input
              type="search"
              placeholder="Search for number, model..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <CustomSelect name="filter" className="form-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="Disponibil">Disponibil</option>
              <option value="Alocat">Alocat</option>
              <option value="Service">Service</option>
            </CustomSelect>
            <CustomSelect name="companyFilter" className="form-select" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}>
              <option value="all">All companies</option>
              {Array.from(new Set(vehicles.map((vehicle) => vehicle.company))).map((company) => (
                <option key={company} value={company}>{company}</option>
              ))}
            </CustomSelect>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body vehicle-table-card">
          <table className="vehicle-table">
            <thead>
              <tr>
                <th>REGISTRATION NUMBER</th>
                <th>BRAND / MODEL</th>
                <th>YEAR / FUEL</th>
                <th>COMPANY</th>
                <th>ASSIGNED DRIVER</th>
                <th>RCA</th>
                <th>ITP</th>
                <th>CASCO</th>
                <th>STATUS</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-row">
                    No vehicles match the current filters.
                  </td>
                </tr>
              ) : (
                filteredVehicles.map((vehicle) => {
                  const renderDoc = (doc) => {
                    const status = doc?.status || 'ok';
                    const label = status === 'critical' ? 'Critical' : status === 'expiring' ? 'Expire' : 'Valid';
                    return (
                      <span className={`status-pill ${status}`}>
                        • {label}
                        {doc?.exp ? ` ${doc.exp}` : ''}
                      </span>
                    );
                  };

                  return (
                    <tr key={vehicle.id || vehicle._id} onClick={() => handleSelectVehicle(vehicle)}>
                      <td className="cell-title">
                        <span className="plate">{vehicle.plate}</span>
                      </td>
                      <td data-label="Brand / Model">
                        <div className="vehicle-name">{vehicle.brand} {vehicle.model}</div>
                        <div className="vehicle-sub">{vehicle.type || 'Vehicle'}</div>
                      </td>
                      <td data-label="Year / Fuel">{vehicle.year} · {vehicle.fuel}</td>
                      <td data-label="Company">{vehicle.company}</td>
                      <td data-label="Assigned Driver">{vehicle.driverName || 'Available'}</td>
                      <td data-label="RCA">{renderDoc(vehicle.docs?.rca)}</td>
                      <td data-label="ITP">{renderDoc(vehicle.docs?.itp)}</td>
                      <td data-label="CASCO">{renderDoc(vehicle.docs?.casco)}</td>
                      <td data-label="Status">
                        <span className={`status-pill ${vehicle.status === 'Disponibil' ? 'available' : vehicle.status === 'Alocat' ? 'assigned' : 'service'}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="row-action cell-action">
                        <button className="btn btn-ghost btn-xs" onClick={(e) => { e.stopPropagation(); handleSelectVehicle(vehicle); }}>
                          Open →
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <VehicleForm
        isOpen={showVehicleForm}
        onClose={() => setShowVehicleForm(false)}
        vehicle={editingVehicle}
        onSave={handleSaveVehicle}
        isTemporary={category === 'temporary'}
      />
    </div>
  );
};

export default Vehicles;
